/**
 * Extract numeric ID from string ID
 * Handles formats like:
 * - "role_k7_123" -> 123
 * - "country.7" -> 7
 * - "8_country.7" -> 7
 *
 * @param id - The string ID to extract numeric value from
 * @returns The numeric ID, or null if not found/invalid
 */
export const extractNumericId = (id: string | undefined): number | null => {
  if (!id) return null;

  // Handle nested geography IDs like "country.7" or "8_country.7"
  const dotParts = id.split('.');
  if (dotParts.length > 1) {
    const lastDotPart = dotParts[dotParts.length - 1];
    if (lastDotPart) {
      const numericId = Number.parseInt(lastDotPart, 10);
      if (Number.isFinite(numericId)) return numericId;
    }
  }

  // Handle standard format like "role_k7_123"
  const parts = id.split('_');
  const lastPart = parts[parts.length - 1];
  if (!lastPart) return null;
  const numericId = Number.parseInt(lastPart, 10);
  return Number.isFinite(numericId) ? numericId : null;
};

/** Recover country ancestry for the provider date floor shared by pages and reads. */
export const countryIdFromNestedGeographyId = (
  id: string | undefined,
  levelId: string
): number | null => {
  if (!id) return null;
  const segments = id.split('_');
  // How far from the END the country sits, by the level the id is at. A level
  // absent from this map has no recoverable country.
  const offsetFromEnd: Record<string, number | undefined> = {
    country: 1,
    msa: 2,
    state: 2,
    city: 3,
    county: 3,
  };
  const offset = offsetFromEnd[levelId];
  if (offset === undefined) return null;
  const country = Number(segments[segments.length - offset]);
  return Number.isInteger(country) && country > 0 ? country : null;
};
